import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { Card } from '@mui/material';
import { useDeletePosition, useGetPositions, useUpdatePositionState } from '../../hooks/usePositions';
import { useTablePageParams } from '../../hooks/useTablePageParams';
import { POSITION_PAGE_FILTERS } from '../../consts';
import STRING_KEYS from '../../language/keys';
import ListPageContainer from '../../components/ListPageContainer';
import { OutlineBriefcase } from '../../icons/outlined-briefcase';
import { PositionStatusEnum, POSITION_STATE } from '../../consts/enums';
import { PositionState } from '../../@types/position';
import PositionTable from './PositionTable';
import PositionFilters from './PositionFilters';

export default function PositionPage():JSX.Element {
  const [error, setError] = useState('');
  const {
    activeFilters,
    onChangeFilter,
    currentPage,
    onPageChange,
    activeSort,
    onSortChange,
  } = useTablePageParams([
    POSITION_PAGE_FILTERS.QUERY,
    POSITION_PAGE_FILTERS.ACCOUNTS,
    POSITION_PAGE_FILTERS.STATUS,
    POSITION_PAGE_FILTERS.STATE,
  ]);

  useEffect(() => {
    onChangeFilter({
      [POSITION_PAGE_FILTERS.STATUS]: PositionStatusEnum.published,
      [POSITION_PAGE_FILTERS.STATE]: POSITION_STATE.OPEN,
    });
    // https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    positions, totalCount = 0, loadPositions,
    refetch,
  } = useGetPositions(activeFilters, currentPage, activeSort);

  const { mutateAsync: deletePosition } = useDeletePosition();
  const { t: translate } = useTranslation();

  const { mutateAsync: updatePositionState } = useUpdatePositionState();

  const handleClosePosition = async (positionId: string, accountId: string, positionState: PositionState) => {
    try {
      await updatePositionState({
        positionId,
        accountId,
        positionState,
      });
      await refetch();
    } catch (e) {
      const err = e as AxiosError;
      const internalErrorCode = err.response?.data.internalErrorCode;
      const errorMessage = translate(STRING_KEYS.POSITION_ERRORS.DELETE, {
        internalErrorCode,
      });
      setError(errorMessage);
    }
  };

  const onDeleteHandler = async (positionId: string, accountId: string) => {
    try {
      await deletePosition({
        positionId,
        accountId,
      });
      await refetch();
    } catch (e) {
      const err = e as AxiosError;
      const internalErrorCode = err.response?.data.internalErrorCode;
      const errorMessage = translate(STRING_KEYS.POSITION_ERRORS.DELETE, {
        internalErrorCode,
      });
      setError(errorMessage);
    }
  };

  const handleChangePage = (newPageNumber: number):void => {
    onPageChange(newPageNumber + 1);
  };

  return (
    <>
      <ListPageContainer
        title={ {
          text: translate(STRING_KEYS.POSITIONS_PAGE_TITLE),
          icon: <OutlineBriefcase />,
        } }
        errors={ error }
      >
        <Card>
          <PositionFilters
            activeFilters={ activeFilters }
            onChangeFilter={ onChangeFilter }
          />
          <PositionTable
            order={ activeSort }
            orderHandler={ onSortChange }
            positions={ positions }
            onPageChange={ handleChangePage }
            onDeleteHandler={ onDeleteHandler }
            onClosePositionHandler={ handleClosePosition }
            page={ currentPage - 1 }
            totalCount={ totalCount }
            isLoading={ loadPositions }
          />
        </Card>
      </ListPageContainer>
    </>
  );
}
